import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SEO from '../../components/SEO';
import FAQ from '../../components/ui/FAQ';
import { aiIntegrationFAQs } from '../../data/faqs';
import { sendRequirementsFormEmail } from '../../utils/emailService';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';

const AIIntegrationLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'AI Integration',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const contactInfo = useContactInfo();

  const seoData = {
    title: 'AI Integration Services - Machine Learning & Automation | OctalChip',
    description: 'AI-powered solutions including machine learning models, chatbots, and intelligent automation. We integrate AI to enhance user experience and automate business processes.',
    keywords: 'AI integration, machine learning, chatbots, predictive analytics, process automation, AI development, OpenAI integration',
    canonical: 'https://www.octalchip.com/landing/ai-integration',
    faqData: aiIntegrationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Integration Services",
      "description": "AI-powered solutions including machine learning models, chatbots, and intelligent automation",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com"
      }
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you within 24 hours with a detailed quote.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: 'AI Integration',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: 'Brain',
      title: 'Machine Learning',
      description: 'Custom ML models for prediction and classification'
    },
    {
      icon: 'MessageCircle',
      title: 'AI Chatbots',
      description: 'Intelligent chatbots with natural language processing'
    },
    {
      icon: 'TrendingUp',
      title: 'Predictive Analytics',
      description: 'Data-driven insights and forecasts for business decisions'
    },
    {
      icon: 'Settings',
      title: 'Process Automation',
      description: 'Automate repetitive tasks to increase efficiency'
    }
  ];

  const technologies = [
    { name: 'Python', description: 'Primary AI/ML language' },
    { name: 'TensorFlow', description: 'Google\'s ML framework' },
    { name: 'PyTorch', description: 'Deep learning framework' },
    { name: 'OpenAI API', description: 'GPT models for NLP' },
    { name: 'Scikit-learn', description: 'Machine learning library' },
    { name: 'Hugging Face', description: 'Pre-trained models' }
  ];

  const useCases = [
    {
      title: 'Customer Support Chatbots',
      description: 'AI-powered chatbots that handle customer inquiries 24/7 with natural language understanding',
      icon: 'MessageCircle'
    },
    {
      title: 'Predictive Analytics',
      description: 'Forecast trends and outcomes to make data-driven business decisions',
      icon: 'TrendingUp'
    },
    {
      title: 'Process Automation',
      description: 'Automate repetitive tasks and workflows to increase efficiency and reduce costs',
      icon: 'Settings'
    },
    {
      title: 'Computer Vision',
      description: 'Image and video analysis for object detection, recognition, and content moderation',
      icon: 'Eye'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
                  AI Integration
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-800">
                  World's Best AI Integration Services - Transform Your Business Globally
                </h1>
                <p className="text-lg md:text-xl mb-8 text-gray-600 leading-relaxed">
                  Trusted by businesses worldwide, we integrate machine learning, chatbots, and intelligent automation into your business. Get AI solutions that enhance user experience, automate processes, and drive growth - recognized globally for excellence.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                  <Button
                    variant="primary"
                    animation="slide"
                    className="px-8 py-4 text-base md:text-lg bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                    onClick={async () => {
                      await scrollToElement('#get-quote', { 
                        smooth: true,
                        maxRetries: 15,
                        retryDelay: 150
                      }).catch(() => {});
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outline"
                    animation="scale"
                    className="px-8 py-4 text-base md:text-lg border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                    onClick={() => navigate('/services/ai-integration')}
                  >
                    View Full Service
                  </Button>
                </div>

                <div className="flex flex-wrap gap-6 text-sm justify-center lg:justify-start">
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Machine Learning</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">AI Chatbots</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Check" size={20} className="text-[#00d690] mr-2" />
                    <span className="text-gray-700">Process Automation</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Get Your Free Quote</h3>
                  <p className="text-gray-600 mb-6">Tell us about your AI project and get a detailed estimate in 24 hours</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Name"
                      required
                    />
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Email"
                      required
                    />
                    <input 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Your Phone"
                      required
                    />
                    <textarea 
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                      placeholder="Describe your AI project..."
                      required
                    ></textarea>
                    
                    {submitMessage && (
                      <div className={`p-4 rounded-lg text-center ${
                        submitMessage.includes('Thank you') 
                          ? 'bg-green-100 text-green-700 border border-green-200' 
                          : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                    
                    <Button 
                      type="submit"
                      variant="primary"
                      animation="float"
                      className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
                    </Button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">50+</div>
              <div className="text-sm md:text-base text-gray-600">AI Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">95%</div>
              <div className="text-sm md:text-base text-gray-600">Model Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">300%</div>
              <div className="text-sm md:text-base text-gray-600">ROI Increase</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#00d690] mb-2">24/7</div>
              <div className="text-sm md:text-base text-gray-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Why Choose Our AI Integration Services?</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We deliver AI solutions that are intelligent, scalable, and optimized for your business
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={32} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our AI Technology Stack</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We use the latest AI and machine learning technologies
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow text-center">
                <h3 className="font-bold text-gray-800 mb-2">{tech.name}</h3>
                <p className="text-sm text-gray-600">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              From chatbots to predictive analytics, we build AI solutions for every business need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={useCase.icon} size={24} className="text-[#00d690]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Common AI Integration Challenges We Solve</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the pain points businesses face. Here's how we help solve them
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Lack of AI Expertise</h3>
                  <p className="text-gray-600">Building AI solutions requires specialized knowledge. We provide expert AI engineers with deep expertise in ML, NLP, and computer vision to build production-ready solutions.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Integration Complexity</h3>
                  <p className="text-gray-600">Integrating AI with existing systems is complex and risky. We handle seamless integration with your CRM, databases, and APIs ensuring no disruption to operations.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Data Quality Issues</h3>
                  <p className="text-gray-600">Poor data quality leads to inaccurate AI models. We clean, prepare, and validate data ensuring high-quality datasets for training accurate AI models.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">High Implementation Costs</h3>
                  <p className="text-gray-600">AI projects can be expensive and risky. We provide cost-effective solutions using pre-trained models, transfer learning, and cloud AI services to reduce costs by 40-60%.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Model Accuracy Problems</h3>
                  <p className="text-gray-600">Low accuracy models fail in production. We develop high-accuracy models with proper training, validation, and testing achieving 90%+ accuracy for production use.</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 border-l-4 border-[#00d690] p-6 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={24} className="text-[#00d690]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Lack of AI Strategy</h3>
                  <p className="text-gray-600">Implementing AI without a clear strategy wastes resources. We provide strategic AI consulting identifying high-impact opportunities and creating roadmaps for successful AI adoption.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our AI Integration Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology that ensures quality, transparency, and on-time delivery
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Discovery & Analysis</h3>
              <p className="text-gray-600">We analyze your business needs and AI opportunities</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">AI Solution Design</h3>
              <p className="text-gray-600">Design custom AI solutions for your use case</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Development & Integration</h3>
              <p className="text-gray-600">Build and integrate AI into your systems</p>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-[#00d690] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4">
                04
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Testing & Deployment</h3>
              <p className="text-gray-600">Comprehensive testing and production deployment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"The AI integration OctalChip built for our platform exceeded expectations. Our customer engagement increased by 300% after implementation."</p>
              <div>
                <p className="font-semibold text-gray-800">James Wilson</p>
                <p className="text-sm text-gray-500">GrowthCorp</p>
              </div>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon key={i} name="Star" size={20} className="text-yellow-400 mr-1" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic leading-relaxed">"OctalChip's AI chatbot transformed our customer support. We automated 70% of inquiries with their intelligent solution."</p>
              <div>
                <p className="font-semibold text-gray-800">Michael Chen</p>
                <p className="text-sm text-gray-500">DataDriven Inc</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our AI Integration Portfolio</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              See how we've helped businesses worldwide integrate AI successfully
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="MessageCircle" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">AI Chatbot Integration</h3>
              <p className="text-gray-600 mb-4">Integrated intelligent chatbot with OpenAI API handling 10,000+ customer inquiries daily. Reduced support costs by 70% and improved response time to under 2 seconds.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">OpenAI</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">NLP</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">REST API</span>
              </div>
              <p className="text-sm text-gray-500">Client: TechFlow Solutions | Location: USA</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="TrendingUp" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Predictive Analytics System</h3>
              <p className="text-gray-600 mb-4">Built ML-powered forecasting system predicting demand with 95% accuracy. Improved inventory management and reduced costs by 25% for retail chain.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">TensorFlow</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">Python</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">MLOps</span>
              </div>
              <p className="text-sm text-gray-500">Client: RetailTech Inc | Location: UK</p>
            </div>
            <div className="bg-gray-50 p-6 md:p-8 rounded-xl hover:shadow-lg transition-shadow border border-gray-200">
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name="Zap" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Process Automation</h3>
              <p className="text-gray-600 mb-4">Automated business processes with AI reducing manual work by 80%. Integrated with existing ERP and CRM systems improving operational efficiency significantly.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">RPA</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">ML</span>
                <span className="text-xs bg-[#00d690]/10 text-[#00d690] px-3 py-1 rounded-full">API Integration</span>
              </div>
              <p className="text-sm text-gray-500">Client: Enterprise Solutions | Location: Canada</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offers Section */}
      <section id="offers" className="py-12 sm:py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">Special Offers & Free Trials</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              Start your AI project with confidence - try our services risk-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">POPULAR</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Play" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Trial</h3>
              <p className="text-gray-600 mb-6">Get 2 hours of free AI development work to experience our quality</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>2 hours of free development</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No commitment required</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep the code you receive</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full project consultation</span></li>
              </ul>
              <Button variant="primary" animation="bounce" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Free Trial</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">NEW</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Gift" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Free Credits</h3>
              <p className="text-gray-600 mb-6">Get $500 in AI development credits when you sign up</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>$500 free credits</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Valid for 30 days</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Deducted from final invoice</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Use on any project</span></li>
              </ul>
              <Button variant="primary" animation="pulse" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Claim Free Credits</Button>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <span className="bg-[#00d690] text-white px-3 py-1 rounded-full text-sm font-medium">GUARANTEE</span>
              </div>
              <div className="w-16 h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6">
                <Icon name="Shield" size={32} className="text-[#00d690]" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">100% Satisfaction</h3>
              <p className="text-gray-600 mb-6">Not satisfied? Get your money back within 7 days</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>7-day money-back guarantee</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>No questions asked</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Keep all deliverables</span></li>
                <li className="flex items-center text-sm text-gray-700"><Icon name="Check" size={16} className="text-[#00d690] mr-2" /><span>Full refund guarantee</span></li>
              </ul>
              <Button variant="primary" animation="scale" className="w-full bg-[#00d690] text-white hover:bg-[#00b876]" onClick={async () => { await scrollToElement('#get-quote', { smooth: true, maxRetries: 15, retryDelay: 150 }).catch(() => {}); }}>Start Risk-Free</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators Section */}
      <section id="trust" className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 sm:gap-12">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">ISO Certified</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="FileText" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">NDA Protected</span>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="CheckCircle" size={24} className="text-[#00d690]" />
              <span className="text-sm sm:text-base font-medium text-gray-700">Money-Back Guarantee</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-quote" className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#f4faf8] via-white to-[#e8f5e8]">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block bg-[#00d690]/10 text-[#00d690] px-4 py-2 rounded-full text-sm font-medium mb-6">
              Start Your AI Project Today
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-gray-800">Ready to Integrate AI Into Your Business?</h2>
            <p className="text-lg md:text-xl mb-8 text-gray-600">
              Get a free consultation and detailed quote for your AI integration project.
            </p>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Name"
                    required
                  />
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                    placeholder="Your Email"
                    required
                  />
                </div>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Your Phone"
                  required
                />
                <textarea 
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-800 focus:border-[#00d690] focus:ring-2 focus:ring-[#00d690]/20"
                  placeholder="Describe your AI integration requirements..."
                  required
                ></textarea>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg text-center ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-[#00d690]/10 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <Button 
                  type="submit"
                  variant="primary"
                  animation="rotate"
                  className="w-full py-3 bg-[#00d690] text-white hover:bg-[#00b876] shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Quote & Consultation'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={aiIntegrationFAQs}
        title="Frequently Asked Questions"
        description="Common questions about our AI integration services."
      />

      <Footer />
    </div>
  );
};

export default AIIntegrationLanding;

